import queryString from "query-string";
import { currencyCodeVar } from "./client";
import sanitizeHtml from 'sanitize-html';
import { I18nManager } from '@shopify/react-i18n';
import precisionByCurrency from "./precisionByCurrency";
export const PlanKindMapping = {
    'ANNUAL': 'year',
    'MONTHLY': 'month',
    'WEEKLY': 'week',
};
export const stripHtml = (html) => { return sanitizeHtml(html, { allowedTags: [], allowedAttributes: {} }); };
export const preventEnter = (e) => {
    if (e.key === 'Enter')
        e.preventDefault();
};
export const handleNumberFieldKeyDown = (e) => {
    if (e.key === 'ArrowUp')
        e.target.innerHTML = parseFloat(e.target.innerHTML) + 1;
    else if (e.key === 'ArrowDown')
        e.target.innerHTML = parseFloat(e.target.innerHTML) - 1 > 0 ? parseFloat(e.target.innerHTML) - 1 : 0;
};
const SHOPIFY_PATTERN = /^([\w\-]+)\.myshopify\.com$/;
export const shopifySubdomain = (input) => {
    const matches = input.match(SHOPIFY_PATTERN);
    return matches[1] || "";
};
const getLocale = () => {
    // https://stackoverflow.com/a/52112155
    // @ts-ignore
    return (navigator.languages && navigator.languages.length) ? navigator.languages[0] : navigator.userLanguage || navigator.language || navigator.browserLanguage || 'en-US';
};
export const getPrecision = (specificCurrencyCode = null) => {
    const currencyCode = specificCurrencyCode || currencyCodeVar();
    return precisionByCurrency.find(p => p.currencyCode == currencyCode) ? precisionByCurrency.find(p => p.currencyCode == currencyCode).precision : 2;
};
export const getExchangeRate = async (specificCurrencyCode = null) => {
    const currencyCode = specificCurrencyCode || currencyCodeVar();
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    let exchangeRate = 1; // default value
    try {
        const response = await fetch(`${process.env.REACT_APP_ASSET_HOST}/api/external/exchange_rates.json`, { signal: controller.signal });
        const data = await response.json();
        // @ts-ignore
        exchangeRate = data.rates[currencyCode] || exchangeRate;
    }
    catch (error) {
        console.log('Failed to fetch exchange rate, using default value: ', error);
    }
    finally {
        clearTimeout(timeoutId);
    }
    return exchangeRate;
};
export const majorToMinorPrice = (price, specificCurrencyCode = null) => {
    const currencyCode = specificCurrencyCode || currencyCodeVar();
    return Math.round(price * 10 ** getPrecision(currencyCode));
};
export const minorToMajorPrice = (price, specificCurrencyCode = null) => {
    const currencyCode = specificCurrencyCode || currencyCodeVar();
    const precision = getPrecision(currencyCode);
    const majorPrice = price / (10 ** precision);
    const roundedPrice = Math.round(majorPrice * (10 ** precision)) / (10 ** precision);
    return roundedPrice;
};
export const formattedPrice = (price, specificCurrencyCode = null, minimizeDecimals = false) => {
    const currencyCode = specificCurrencyCode || currencyCodeVar();
    const majorUnits = minorToMajorPrice(price, currencyCode);
    const formatted = new Intl.NumberFormat(getLocale(), {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: minimizeDecimals && Number.isInteger(majorUnits) ? 0 : 2
    }).format(majorUnits);
    return formatted;
};
export const i18nManager = () => new I18nManager({
    locale: "en",
    currency: currencyCodeVar()
});
export const GID_PATTERN = /gid:\/\/shopify\/\w+\/(\d+)$/;
export const parseGid = (gid) => {
    return gid.match(GID_PATTERN)[1];
};
export const joinLinkFor = (alias, channel, query) => {
    let link = `${process.env.REACT_APP_WITHFRIENDS_HOST}/${alias}/join`;
    let baseQuery = queryString.stringify(query);
    if (channel) {
        link = `${link}:channel=${channel}`;
    }
    if (baseQuery) {
        link = `${link}?${baseQuery}`;
    }
    return link.toLowerCase();
};
const benefitFulfillmentSettings = (benefit) => {
    const fulfillmentSettings = benefit.deliveredToYour === 'inbox' ? {
        digital: {
            enabled: true
        },
        shipping: {
            enabled: false
        },
        pickup: {
            enabled: false
        }
    } : {
        digital: {
            enabled: false
        },
        shipping: {
            enabled: true,
            cost: benefit.shippingIncluded ? 'included' : 'calculated'
        },
        pickup: {
            enabled: false
        }
    };
    if (benefit.id == "20385356") { // PSYCHIC DAMAGE: Silver Sprocket
        fulfillmentSettings.digital.enabled = true;
        fulfillmentSettings.shipping = { enabled: false };
        fulfillmentSettings.shipping = { enabled: false };
    }
    if (benefit.id === "22189056") { // Psychic damage: Arvida Book Co
        fulfillmentSettings.pickup.enabled = true;
        fulfillmentSettings.shipping.cost = 'addon';
        // @ts-ignore
        fulfillmentSettings.shipping.addonIds = ['8367e005-2ba3-40f1-879c-296c4c778108'];
    }
    if (["22204675", "22204674", "22204635"].includes(benefit.id)) { // Psychic damage: Arvida Book Co
        fulfillmentSettings.pickup = { enabled: true };
        fulfillmentSettings.shipping = { enabled: false };
    }
    return fulfillmentSettings;
};
export const benefitFulfillmentText = (benefit) => {
    const fulfillmentSettings = benefitFulfillmentSettings(benefit);
    let mode;
    if (fulfillmentSettings.digital.enabled) {
        mode = 'digital';
    }
    else if (fulfillmentSettings.pickup.enabled && fulfillmentSettings.shipping.enabled) {
        mode = 'both';
    }
    else if (fulfillmentSettings.pickup.enabled) {
        mode = 'pickup';
    }
    else {
        mode = 'shipping';
    }
    const grammarMap = {
        subscription_box_benefit_line: {
            digital: 'available in your inbox',
            shipping: 'delivered to your door',
            pickup: 'available for pickup',
            both: 'available for pickup or delivery'
        },
        welcome_gift_benefit_line: {
            digital: 'sent to you',
            shipping: 'sent to you',
            pickup: 'available for pickup starting',
            both: 'available for pickup or delivery, starting'
        },
        subscription_box_next_box: {
            digital: 'will send',
            shipping: 'ships',
            pickup: 'is ready',
            both: 'is ready'
        },
        welcome_gift_next_box: {
            digital: 'will send',
            shipping: 'ships',
            pickup: 'is ready',
            both: 'is ready'
        },
        fulfillment_date: {
            digital: 'Available Online Date',
            shipping: 'Shipping Date',
            pickup: 'Pickup Date',
            both: 'Available Date'
        },
        next_fulfillment_date: {
            digital: 'Next Available Date',
            shipping: 'Next Shipment Date',
            pickup: 'Next Pickup Date',
            both: 'Next Available Date'
        }
    };
    return (grammar) => { var _a; return (_a = grammarMap[grammar]) === null || _a === void 0 ? void 0 : _a[mode]; };
};
